/** Fila por identidade e ambiente. Payload e metadados de negócio ficam cifrados em repouso. */
import { z } from 'zod';
import { ref, readonly } from 'vue';
import { ApiError, getSession, isLogoutPending, loadEnv, onSessionChange } from '@/core';
import { uploadPhoto } from '@/api/services';
import { closeServiceOrder, type CloseServiceOrderInput } from '@/api/serviceOrder';
import { ensureSession } from '@/stores/session.store';

const DB_NAME = 'dt_offline_queue';
const STORE = 'pending';
const KEYS = 'keys';
const ItemSchema = z.object({
  id: z.string().uuid(), scope: z.string(), createdAt: z.number(),
  state: z.enum(['pending', 'sending', 'authentication', 'retry', 'intervention']),
  attempts: z.number().int().nonnegative(), nextAttemptAt: z.number(),
  message: z.string(), encrypted: z.instanceof(ArrayBuffer), iv: z.instanceof(Uint8Array),
});
type Item = z.infer<typeof ItemSchema>;
const CloseInputSchema = z.object({
  draft: z.object({ diagnosis: z.string().nullable(), servicePerformed: z.string().nullable(), timeSpentMinutes: z.number().nullable(),
    parts: z.array(z.object({ description: z.string(), quantity: z.number(), unitAmount: z.number(),
      isNoCharge: z.boolean().default(false), noChargeReason: z.string().default('') })).nullable(),
    recommendations: z.string().nullable().default(null), nextMaintenance: z.string().nullable().default(null),
  }).optional(),
  signerName: z.string().min(1), signerDataUrl: z.string().min(1),
  signerLatitude: z.number().nullable(), signerLongitude: z.number().nullable(),
  signerLocationAccuracyMeters: z.number().nullable(), signerLocationAbsenceReason: z.string().nullable(),
  acceptedClinicAmount: z.number().nullable(), recommendations: z.string().nullish().default(null),
  nextMaintenance: z.string().nullish().default(null),
});
const PayloadSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('photo'), requestId: z.string(), photoType: z.enum(['ANTES', 'DEPOIS']),
    fileName: z.string(), mime: z.string(), bytes: z.array(z.number().int().min(0).max(255)) }),
  z.object({ kind: z.literal('close'), requestId: z.string(), input: CloseInputSchema }),
]);
type Payload = z.infer<typeof PayloadSchema>;
export interface OfflineQueueEntry {
  readonly id: string; readonly state: Item['state']; readonly message: string;
  readonly requestId: string | null; readonly label: string; readonly createdAt: number;
}
const entries = ref<OfflineQueueEntry[]>([]);
const queueError = ref<string | null>(null);
const legacyCount = ref(0);
let timer: ReturnType<typeof setTimeout> | undefined;

export function useOfflineQueue() {
  return { entries: readonly(entries), error: readonly(queueError), legacyCount: readonly(legacyCount),
    retry: flush, discard: discardItem };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 2);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE, { keyPath: 'id' });
      if (!request.result.objectStoreNames.contains(KEYS)) request.result.createObjectStore(KEYS);
    };
    request.onsuccess = () => { resolve(request.result); };
    request.onerror = () => { reject(new Error('Não foi possível abrir a fila guardada neste dispositivo.')); };
  });
}

async function listRaw(): Promise<unknown[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).getAll();
    tx.oncomplete = () => { db.close(); resolve(z.array(z.unknown()).parse(request.result)); };
    tx.onerror = () => { db.close(); reject(new Error('Não foi possível ler a fila.')); };
  });
}

async function writeItem(item: Item | string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    if (typeof item === 'string') tx.objectStore(STORE).delete(item);
    else tx.objectStore(STORE).put(item);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onabort = tx.onerror = () => { db.close(); reject(new Error('Não foi possível gravar a fila.')); };
  });
}

async function scopeForCurrentUser(): Promise<string> {
  const user = getSession().user;
  if (user === null || user.persona !== 'tecnicos' || isLogoutPending()) throw new Error('Entre na sua conta para acessar a fila.');
  const bytes = new TextEncoder().encode(`${loadEnv().VITE_API_BASE_URL}|${user.persona}|${user.id}`);
  const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  return Array.from(hash, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function keyForScope(scope: string): Promise<CryptoKey> {
  // Geração fora da transação: escolher/criar é atômico mesmo com duas abas enfileirando juntas.
  const candidate = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(KEYS, 'readwrite');
    const store = tx.objectStore(KEYS);
    const request = store.get(scope);
    let selected = candidate;
    request.onsuccess = () => {
      const value: unknown = request.result;
      if (value instanceof CryptoKey && !value.extractable && value.algorithm.name === 'AES-GCM') selected = value;
      else if (value === undefined) store.put(candidate, scope);
      else tx.abort();
    };
    tx.oncomplete = () => { db.close(); resolve(selected); };
    tx.onabort = tx.onerror = () => { db.close(); reject(new Error('Não foi possível recuperar a chave da fila.')); };
  });
}

function buffer(bytes: Uint8Array): ArrayBuffer { return new Uint8Array(bytes).buffer; }

async function scopedItems(scope: string): Promise<Item[]> {
  const parsed = (await listRaw()).map((item) => ItemSchema.safeParse(item));
  // Legado v1 perdeu sua chave em memória. Preservado, nunca apresentado como enviado.
  legacyCount.value = parsed.filter((item) => !item.success).length;
  return parsed.flatMap((item) => item.success && item.data.scope === scope ? [item.data] : [])
    .sort((left, right) => left.createdAt - right.createdAt);
}

async function refreshEntries(scope: string): Promise<Item[]> {
  const items = await scopedItems(scope);
  const key = items.length ? await keyForScope(scope) : null;
  const visible = await Promise.all(items.map(async (item): Promise<OfflineQueueEntry> => {
    const base = { id: item.id, state: item.state, message: item.message, createdAt: item.createdAt };
    try {
      if (key === null) throw new Error('Chave indisponível.');
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buffer(item.iv) }, key, item.encrypted);
      const payload = PayloadSchema.parse(JSON.parse(new TextDecoder().decode(plain)));
      return { ...base, requestId: payload.requestId, label: payload.kind === 'close' ? 'Fechamento da ordem'
        : payload.photoType === 'ANTES' ? 'Foto antes do atendimento' : 'Foto depois do atendimento' };
    } catch { return { ...base, requestId: null, label: 'Captura não recuperável' }; }
  }));
  if (await scopeForCurrentUser() === scope) entries.value = visible;
  return items;
}

async function enqueue(payload: Payload): Promise<{ sent: boolean }> {
  const scope = await scopeForCurrentUser();
  const key = await keyForScope(scope);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(JSON.stringify(payload)));
  if (await scopeForCurrentUser() !== scope) throw new Error('A conta mudou. Salve novamente na conta correta.');
  const id = crypto.randomUUID();
  if (!('locks' in navigator)) throw new Error('Este navegador não permite guardar a fila com segurança entre abas.');
  await navigator.locks.request(`dt-offline-${scope}`, async () => {
    if (payload.kind === 'close') {
      for (const existing of await scopedItems(scope)) {
        let previous: Payload;
        try {
          const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buffer(existing.iv) }, key, existing.encrypted);
          previous = PayloadSchema.parse(JSON.parse(new TextDecoder().decode(plain)));
        } catch {
          // Registro indecifrável é tratado na reconciliação sem ser removido.
          continue;
        }
        if (previous.kind === 'close' && previous.requestId === payload.requestId) {
          throw new Error('Esta ordem já está guardada. Use a fila de envios para tentar novamente; não capture outra assinatura.');
        }
      }
    }
    await writeItem({ id, scope, createdAt: Date.now(), state: 'pending',
      attempts: 0, nextAttemptAt: 0, message: 'Guardado neste dispositivo. Aguardando envio.', encrypted, iv });
  });
  await refreshEntries(scope);
  const result = await flush();
  // Ausência no IndexedDB também pode significar descarte em outra aba; só ACK prova envio.
  return { sent: result.confirmedIds.includes(id) };
}

export async function enqueuePhoto(requestId: string, photoType: 'ANTES' | 'DEPOIS', fileName: string, file: File): Promise<{ sent: boolean }> {
  return enqueue({ kind: 'photo', requestId, photoType, fileName, mime: file.type, bytes: Array.from(new Uint8Array(await file.arrayBuffer())) });
}

export async function enqueueClose(requestId: string, input: CloseServiceOrderInput): Promise<{ sent: boolean }> {
  return enqueue({ kind: 'close', requestId, input: CloseInputSchema.parse(input) });
}

async function discardItem(id: string): Promise<void> {
  const scope = await scopeForCurrentUser();
  if (!('locks' in navigator)) throw new Error('Este navegador não permite coordenar a fila entre abas.');
  await navigator.locks.request(`dt-offline-${scope}`, async () => {
    if ((await scopedItems(scope)).some((item) => item.id === id)) await writeItem(id);
    await refreshEntries(scope);
  });
}

async function sendItems(scope: string, confirmedIds: string[]): Promise<void> {
  const key = await keyForScope(scope);
  for (const item of await scopedItems(scope)) {
    // Preserva a ordem: uma foto pendente não pode ser ultrapassada pelo fechamento da OS.
    if (item.state === 'intervention' || item.nextAttemptAt > Date.now()) break;
    if (await scopeForCurrentUser() !== scope) break;
    let payload: Payload;
    try {
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buffer(item.iv) }, key, item.encrypted);
      payload = PayloadSchema.parse(JSON.parse(new TextDecoder().decode(plain)));
    } catch {
      await writeItem({ ...item, state: 'intervention', message: 'Não foi possível recuperar este registro. Ele não foi enviado. Refaça a captura antes de descartá-lo.' });
      break;
    }
    await writeItem({ ...item, state: 'sending', message: 'Enviando…' });
    try {
      if (await scopeForCurrentUser() !== scope) break;
      if (payload.kind === 'photo') {
        const file = new File([buffer(new Uint8Array(payload.bytes))], payload.fileName, { type: payload.mime });
        await uploadPhoto(payload.requestId, payload.photoType, payload.fileName, file, item.id);
      } else {
        const { draft, ...input } = payload.input;
        await closeServiceOrder(payload.requestId, { ...input, ...(draft === undefined ? {} : { draft }) }, item.id);
      }
      // Somente confirmação bem-sucedida remove o registro; chave idempotente sobrevive à recarga.
      await writeItem(item.id);
      confirmedIds.push(item.id);
    } catch (cause) {
      const status = cause instanceof ApiError ? cause.status : 0;
      const authentication = status === 401 || status === 403;
      const recoverable = status === 0 || authentication || status === 429 || status >= 500;
      const attempts = item.attempts + 1;
      const delay = Math.min(300_000, 5_000 * 2 ** Math.min(attempts, 6));
      const retryAfter = cause instanceof ApiError ? cause.retryAfterMs : undefined;
      await writeItem({ ...item, attempts, nextAttemptAt: authentication ? 0 : Date.now() + Math.max(delay, retryAfter ?? 0),
        state: authentication ? 'authentication' : recoverable ? 'retry' : 'intervention',
        message: authentication ? 'Confirme seu acesso e tente novamente. O registro continua guardado.'
          : recoverable ? 'Envio não confirmado. O registro continua guardado para nova tentativa.'
            : 'O servidor rejeitou este registro. Revise o atendimento e refaça a captura antes de descartá-lo.',
      });
      break;
    }
  }
}

export async function flush(): Promise<{ pending: number; confirmedIds: readonly string[] }> {
  const confirmedIds: string[] = [];
  if (timer !== undefined) clearTimeout(timer);
  try {
    const scope = await scopeForCurrentUser();
    await refreshEntries(scope);
    if (!('locks' in navigator)) throw new Error('Este navegador não permite enviar a fila com segurança entre abas. Os registros continuam guardados.');
    if ((await ensureSession()).status !== 'authenticated' || await scopeForCurrentUser() !== scope) return { pending: entries.value.length, confirmedIds };
    await navigator.locks.request(`dt-offline-${scope}`, { ifAvailable: true }, async (lock) => {
      if (lock !== null) await sendItems(scope, confirmedIds);
    });
    const items = await refreshEntries(scope);
    queueError.value = null;
    const first = items[0];
    if (first !== undefined && first.state !== 'intervention' && first.state !== 'authentication') {
      timer = setTimeout(() => { void flush(); }, Math.max(5_000, first.nextAttemptAt - Date.now()));
    }
    return { pending: items.length, confirmedIds };
  } catch (cause) {
    queueError.value = cause instanceof Error ? cause.message : 'Não foi possível acessar a fila.';
    return { pending: entries.value.length, confirmedIds };
  }
}

onSessionChange(() => { entries.value = []; legacyCount.value = 0; if (timer !== undefined) clearTimeout(timer); });
if (typeof window !== 'undefined') window.addEventListener('online', () => { void flush(); });
